// JQ4-45 · US-AUDIT-005 — AuditLogTable + AuditLogDetail
//
// Renders the audit log result list and detail panel on row click.
// Spec refs: R-LIST-01 to R-LIST-05, R-DETAIL-01 to R-DETAIL-04, R-EMPTY-01 to R-EMPTY-03.

import { useTranslation } from '@jq4race/i18n';
import type { AuditLogEntryResponse } from '@jq4race/shared';
import {
  Button,
  Card,
  CardContent,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@jq4race/ui';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';

import { formatTimestamp } from '../../../../shared/format/format-timestamp';
import { resolveActionLabel, resolveSummary } from '../../lib/resolveSummary';

// ── Helpers ───────────────────────────────────────────────────────────────────

const ROLE_LABEL_KEYS: Record<string, string> = {
  super_admin: 'roles.super_admin',
  org_admin: 'roles.org_admin',
  supervisor: 'roles.supervisor',
  operator: 'roles.operator',
};

// Human-readable actor label: "email · Role", falling back gracefully.
// - No actor (actorUserId null) → system-originated event ("System"), UNLESS
//   metadata identifies a station principal (valet-keychain-qr D1 — a
//   StationDevice.id is not an identity_users row, so actorUserId is left
//   null and the station identity is carried in metadata instead; see
//   station-lookup.use-case.ts). Falling back to "System" for a station
//   action would violate the audit spec's "not anonymous, not System"
//   requirement, so this is checked BEFORE the null-actor "System" case.
// - Actor set but unresolved (deleted user) → short ID so it's still traceable.
// `t` is passed in (rather than this being a hook itself) so it can be reused
// from both the main table rows and the AuditLogDetail panel below.
function resolveActor(entry: AuditLogEntryResponse, t: (key: string) => string): string {
  const metadata = entry.metadata as Record<string, unknown> | undefined;
  if (metadata?.principalType === 'station' && typeof metadata.stationLabel === 'string') {
    return `${t('station')} · ${metadata.stationLabel}`;
  }
  if (!entry.actorUserId) return t('system');
  const roleKey = entry.actorRole ? ROLE_LABEL_KEYS[entry.actorRole] : undefined;
  const role = entry.actorRole ? (roleKey ? t(roleKey) : entry.actorRole) : null;
  if (entry.actorEmail) {
    return role ? `${entry.actorEmail} · ${role}` : entry.actorEmail;
  }
  const shortId = `${entry.actorUserId.slice(0, 8)}…`;
  return role ? `${shortId} · ${role}` : shortId;
}

// ── Detail panel ──────────────────────────────────────────────────────────────

interface DetailFieldProps {
  label: string;
  value: React.ReactNode;
}

function DetailField({ label, value }: DetailFieldProps) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2 border-b border-jq-sand/30 py-2 last:border-0">
      <dt className="text-[12px] font-medium uppercase tracking-[0.12em] text-jq-graphite/70">
        {label}
      </dt>
      <dd className="break-all text-[13px] text-jq-charcoal">{value ?? '—'}</dd>
    </div>
  );
}

interface AuditLogDetailProps {
  entry: AuditLogEntryResponse;
  onClose: () => void;
  /** IANA timezone to render timestamps in; defaults to the browser's. */
  timeZone?: string;
}

export function AuditLogDetail({ entry, onClose, timeZone }: AuditLogDetailProps) {
  const { t, i18n } = useTranslation('audit-log');
  const metadata = entry.metadata ?? {};
  const before = metadata.before as Record<string, unknown> | null | undefined;
  const after = metadata.after as Record<string, unknown> | null | undefined;
  // Collect reference IDs from metadata (any key ending in "Id")
  const refIds = Object.entries(metadata).filter(
    ([k]) => k !== 'before' && k !== 'after' && k.endsWith('Id'),
  );
  // Extra metadata (neither before/after nor id refs)
  const extraMeta = Object.entries(metadata).filter(
    ([k]) => k !== 'before' && k !== 'after' && !k.endsWith('Id'),
  );

  return (
    <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
      <CardContent className="p-6 sm:p-8">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-[15px] font-medium text-jq-charcoal">{t('detail.title')}</h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-[13px]">
            {t('detail.close')}
          </Button>
        </div>
        <dl>
          <DetailField label={t('detail.eventId')} value={entry.id} />
          <DetailField
            label={t('detail.timestamp')}
            value={formatTimestamp(entry.createdAt, i18n.language, timeZone, { seconds: true })}
          />
          <DetailField label={t('detail.action')} value={entry.action} />
          <DetailField label={t('detail.actor')} value={resolveActor(entry, t)} />
          <DetailField label={t('detail.actorUserId')} value={entry.actorUserId ?? '—'} />
          <DetailField label={t('detail.resourceType')} value={entry.resourceType} />
          <DetailField label={t('detail.resourceId')} value={entry.resourceId ?? '—'} />
          <DetailField label={t('detail.organizationId')} value={entry.organizationId ?? '—'} />
          <DetailField label={t('detail.ip')} value={entry.ip ?? '—'} />
          <DetailField label={t('detail.userAgent')} value={entry.userAgent ?? '—'} />

          {/* Before state — R-DETAIL-02, R-DETAIL-03 */}
          <DetailField
            label={t('detail.before')}
            value={
              before != null ? (
                <pre className="max-h-40 overflow-auto rounded bg-jq-surface-1 p-2 text-[11px]">
                  {JSON.stringify(before, null, 2)}
                </pre>
              ) : (
                '—'
              )
            }
          />

          {/* After state — R-DETAIL-02, R-DETAIL-03 */}
          <DetailField
            label={t('detail.after')}
            value={
              after != null ? (
                <pre className="max-h-40 overflow-auto rounded bg-jq-surface-1 p-2 text-[11px]">
                  {JSON.stringify(after, null, 2)}
                </pre>
              ) : (
                '—'
              )
            }
          />

          {/* Reference IDs from metadata — key is a raw API field name, not translated */}
          {refIds.map(([key, val]) => (
            <DetailField key={key} label={key} value={String(val)} />
          ))}

          {/* Extra metadata fields */}
          {extraMeta.length > 0 && (
            <DetailField
              label={t('detail.metadata')}
              value={
                <pre className="max-h-40 overflow-auto rounded bg-jq-surface-1 p-2 text-[11px]">
                  {JSON.stringify(Object.fromEntries(extraMeta), null, 2)}
                </pre>
              }
            />
          )}
        </dl>
      </CardContent>
    </Card>
  );
}

// ── Main table ────────────────────────────────────────────────────────────────

interface AuditLogTableProps {
  items: AuditLogEntryResponse[];
  isLoading: boolean;
  hasMore: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  /**
   * IANA timezone to render timestamps in. Audit entries are org-wide and carry
   * no location, so the container picks one (active location, else the
   * browser's — the default when omitted). Never UTC.
   */
  timeZone?: string;
}

export function AuditLogTable({
  items,
  isLoading,
  hasMore,
  isFetchingNextPage,
  onLoadMore,
  timeZone,
}: AuditLogTableProps) {
  const { t, i18n } = useTranslation('audit-log');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const expandedEntry = items.find((i) => i.id === expandedId) ?? null;

  function toggleExpanded(id: string) {
    setExpandedId((prev) => (prev === id ? null : id));
  }

  if (isLoading) {
    return (
      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('table.timestamp')}</TableHead>
                <TableHead>{t('table.eventType')}</TableHead>
                <TableHead>{t('table.actor')}</TableHead>
                <TableHead>{t('table.summary')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: loading skeleton rows
                <TableRow key={`skel-${i}`}>
                  {Array.from({ length: 4 }).map((_, j) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: loading skeleton cells
                    <TableCell key={`cell-${j}`}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[180px]">{t('table.timestamp')}</TableHead>
                <TableHead>{t('table.eventType')}</TableHead>
                <TableHead className="w-[220px]">{t('table.actor')}</TableHead>
                <TableHead>{t('table.summary')}</TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                /* R-EMPTY-01 — empty state message */
                <TableEmpty colSpan={5}>
                  <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
                    <p className="text-[14px] font-medium text-jq-charcoal">{t('empty')}</p>
                  </div>
                </TableEmpty>
              ) : (
                items.map((entry) => (
                  <TableRow
                    key={entry.id}
                    onClick={() => toggleExpanded(entry.id)}
                    className="cursor-pointer hover:bg-jq-surface-1/50"
                  >
                    <TableCell className="font-mono text-[12px] text-jq-graphite">
                      {formatTimestamp(entry.createdAt, i18n.language, timeZone, { seconds: true })}
                    </TableCell>
                    <TableCell className="text-[13px] text-jq-charcoal">
                      {resolveActionLabel(entry.action, t)}
                    </TableCell>
                    <TableCell className="truncate text-[13px] text-jq-graphite">
                      {resolveActor(entry, t)}
                    </TableCell>
                    <TableCell className="text-[13px] text-jq-graphite">
                      {resolveSummary(entry, t)}
                    </TableCell>
                    <TableCell className="text-jq-stone">
                      {expandedId === entry.id ? (
                        <ChevronDown className="h-4 w-4" strokeWidth={1.5} />
                      ) : (
                        <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* R-DETAIL-01 — expanded row detail (R-DETAIL-01 to R-DETAIL-04) */}
      {expandedEntry && (
        <AuditLogDetail
          entry={expandedEntry}
          onClose={() => setExpandedId(null)}
          timeZone={timeZone}
        />
      )}

      {/* R-LIST-05 — Load more control */}
      {hasMore && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
            className="text-[13px]"
          >
            {isFetchingNextPage ? t('common:states.loading') : t('loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
}
